import { AddRounded, BorderClearOutlined } from '@mui/icons-material'
import BorderAllOutlinedIcon from '@mui/icons-material/BorderAllOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TextureIcon from '@mui/icons-material/Texture'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { EditorProps, IReportTableColumn, IReportTableRow, SectionColumnSettingsMap, TableBorderStyle } from '../../../definitions/report_builder/ReportBuilderDefinitions'
import CustomTextField from '../../CustomTextField'
import BorderStyleEditor from './BorderStyleEditor'
import TextTableEditor from './TextTableEditor'

const TableEditor = ({ column, onChange }: EditorProps<'table'>) => {
    const settings = column.settings as SectionColumnSettingsMap['table']
    const [draggedRowIndex, setDraggedRowIndex] = useState<number>(null)
    const [dragOverRowIndex, setDragOverRowIndex] = useState<number>(null)
    const [draggedColumnIndex, setDraggedColumnIndex] = useState<number>(null)
    const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number>(null)
    const iconSize = '30px'

    const handleColumnChange = (index: number, updatedCol: IReportTableColumn) => {
        const newColumns = [...(settings.columns ?? [])]
        if (updatedCol === null) {
            newColumns.splice(index, 1)
        } else {
            newColumns[index] = updatedCol
        }
        onChange({ ...settings, columns: newColumns })
    }

    const handleRowChange = (index: number, updatedRow: IReportTableRow) => {
        const newRows = [...(settings.rows ?? [])]
        if (updatedRow === null) {
            newRows.splice(index, 1)
        } else {
            newRows[index] = updatedRow
        }
        onChange({ ...settings, rows: newRows })
    }

    const handleGeneralStyleChange = (borderStyle: TableBorderStyle) => {
        const resetColumns = (settings.columns ?? []).map(col => ({
            ...col,
            borders: undefined
        }))

        const resetRows = (settings.rows ?? []).map(row => ({
            ...row,
            borders: undefined
        }))

        onChange({
            ...settings,
            generalStyle: {
                borderStyle
            },
            columns: resetColumns,
            rows: resetRows
        })
    }

    const addDataSet = () => {
        const newRows = [...(settings.rows ?? []), {
            value: `Data ${(settings.rows?.length ?? 0) + 1}`,
            isSpacer: false
        }]
        onChange({ ...settings, rows: newRows })
    }

    const addSpacer = () => {
        const newRows = [...(settings.rows ?? []), {
            value: '',
            isSpacer: true
        }]
        onChange({ ...settings, rows: newRows })
    }

    const handleRowDragStart = (e: React.DragEvent, index: number) => {
        setDraggedRowIndex(index)
        e.dataTransfer.effectAllowed = 'move'

        const originalElement = e.currentTarget as HTMLElement
        const dragImage = originalElement.cloneNode(true) as HTMLElement

        dragImage.style.position = 'absolute'
        dragImage.style.top = '-1000px'
        dragImage.style.left = '-1000px'
        dragImage.style.width = `${Math.min(originalElement.offsetWidth, 600)}px`
        dragImage.style.transform = 'rotate(2deg)'
        dragImage.style.opacity = '0.8'
        dragImage.style.pointerEvents = 'none'
        dragImage.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'
        dragImage.style.borderRadius = '8px'
        dragImage.style.zIndex = '9999'

        document.body.appendChild(dragImage)

        const rect = originalElement.getBoundingClientRect()
        e.dataTransfer.setDragImage(dragImage, rect.width / 2, 20)

        setTimeout(() => {
            if (document.body.contains(dragImage)) {
                document.body.removeChild(dragImage)
            }
        }, 100)
    }

    const handleRowDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDragOverRowIndex(index)
    }

    const handleRowDragLeave = () => {
        setDragOverRowIndex(null)
    }

    const handleRowDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault()

        if (draggedRowIndex === null || draggedRowIndex === targetIndex) {
            setDraggedRowIndex(null)
            setDragOverRowIndex(null)
            return
        }

        const rows = [...(settings.rows ?? [])]
        const draggedRow = rows[draggedRowIndex]

        rows.splice(draggedRowIndex, 1)
        const adjustedTargetIndex = draggedRowIndex < targetIndex ? targetIndex - 1 : targetIndex
        rows.splice(adjustedTargetIndex, 0, draggedRow)

        onChange({ ...settings, rows })
        setDraggedRowIndex(null)
        setDragOverRowIndex(null)
    }

    const handleRowDragEnd = () => {
        setDraggedRowIndex(null)
        setDragOverRowIndex(null)
    }

    const handleColumnDragStart = (e: React.DragEvent, index: number) => {
        setDraggedColumnIndex(index)
        e.dataTransfer.effectAllowed = 'move'

        const originalElement = e.currentTarget as HTMLElement
        const dragImage = originalElement.cloneNode(true) as HTMLElement

        dragImage.style.position = 'absolute'
        dragImage.style.top = '-1000px'
        dragImage.style.left = '-1000px'
        dragImage.style.width = `${Math.min(originalElement.offsetWidth, 600)}px`
        dragImage.style.transform = 'rotate(2deg)'
        dragImage.style.opacity = '0.8'
        dragImage.style.pointerEvents = 'none'
        dragImage.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'
        dragImage.style.borderRadius = '8px'
        dragImage.style.zIndex = '9999'

        document.body.appendChild(dragImage)

        const rect = originalElement.getBoundingClientRect()
        e.dataTransfer.setDragImage(dragImage, rect.width / 2, 20)

        setTimeout(() => {
            if (document.body.contains(dragImage)) {
                document.body.removeChild(dragImage)
            }
        }, 100)
    }

    const handleColumnDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDragOverColumnIndex(index)
    }

    const handleColumnDragLeave = () => {
        setDragOverColumnIndex(null)
    }

    const handleColumnDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault()

        if (draggedColumnIndex === null || draggedColumnIndex === targetIndex) {
            setDraggedColumnIndex(null)
            setDragOverColumnIndex(null)
            return
        }

        const columns = [...(settings.columns ?? [])]
        const draggedColumn = columns[draggedColumnIndex]

        columns.splice(draggedColumnIndex, 1)
        const adjustedTargetIndex = draggedColumnIndex < targetIndex ? targetIndex - 1 : targetIndex
        columns.splice(adjustedTargetIndex, 0, draggedColumn)

        onChange({ ...settings, columns })
        setDraggedColumnIndex(null)
        setDragOverColumnIndex(null)
    }

    const handleColumnDragEnd = () => {
        setDraggedColumnIndex(null)
        setDragOverColumnIndex(null)
    }

    const currentBorderStyle = settings.generalStyle?.borderStyle ?? 'none'

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 2 }}>
                        General Layout
                    </Typography>

                    <Typography variant='caption' sx={{ minWidth: 20, mb: 1, display: 'block', color: 'text.secondary' }}>
                        Default table borders:
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Tooltip title='No borders (default)' placement='top'>
                            <IconButton
                                size='medium'
                                onClick={() => handleGeneralStyleChange('none')}
                                sx={{
                                    'height': iconSize,
                                    'width': iconSize,
                                    'border': 1,
                                    'borderColor': currentBorderStyle === 'none' ? 'primary.main' : 'divider',
                                    'borderRadius': 1,
                                    'bgcolor': currentBorderStyle === 'none' ? 'action.selected' : 'transparent',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: currentBorderStyle === 'none' ? 'action.selected' : 'action.hover'
                                    }
                                }}>
                                <BorderClearOutlined />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title='All borders' placement='top'>
                            <IconButton
                                size='medium'
                                onClick={() => handleGeneralStyleChange('all')}
                                sx={{
                                    'height': iconSize,
                                    'width': iconSize,
                                    'border': 1,
                                    'borderColor': currentBorderStyle === 'all' ? 'primary.main' : 'divider',
                                    'borderRadius': 1,
                                    'bgcolor': currentBorderStyle === 'all' ? 'action.selected' : 'transparent',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: currentBorderStyle === 'all' ? 'action.selected' : 'action.hover'
                                    }
                                }}>
                                <BorderAllOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                        <Typography variant='subtitle2'>Table Data Set</Typography>
                        <Box display='flex' gap={1}>
                            <Button
                                startIcon={<TextureIcon />}
                                size='small'
                                variant='outlined'
                                color='secondary'
                                onClick={addSpacer}>
                                Spacer
                            </Button>
                            <Button
                                startIcon={<AddRounded />}
                                size='small'
                                variant='outlined'
                                onClick={addDataSet}>
                                Data Set
                            </Button>
                        </Box>
                    </Box>
                    {(settings.rows ?? []).map((row: IReportTableRow, idx: number) => (
                        <Box
                            key={`row-${idx}`}
                            sx={{
                                'position': 'relative',
                                'transition': 'all 0.2s ease-in-out',
                                'transform': dragOverRowIndex === idx ? 'translateY(-4px)' : 'translateY(0)',
                                '&::before': dragOverRowIndex === idx
                                    ? {
                                            content: '""',
                                            position: 'absolute',
                                            top: -2,
                                            left: 0,
                                            right: 0,
                                            height: 4,
                                            backgroundColor: 'primary.main',
                                            borderRadius: 2,
                                            zIndex: 1000
                                        }
                                    : {}
                            }}
                            onDragOver={e => handleRowDragOver(e, idx)}
                            onDragLeave={handleRowDragLeave}
                            onDrop={e => handleRowDrop(e, idx)}>
                            <Box
                                draggable
                                onDragStart={e => handleRowDragStart(e, idx)}
                                onDragEnd={handleRowDragEnd}
                                sx={{
                                    'cursor': 'grab',
                                    '&:active': {
                                        cursor: 'grabbing'
                                    },
                                    'opacity': draggedRowIndex === idx ? 0.4 : 1,
                                    'transition': 'opacity 0.2s ease-in-out',
                                    '&:hover': {
                                        '.report-section-commands': { opacity: 1 },
                                        '.drag-handle': { opacity: 1 },
                                        'boxShadow': '10px 0 15px rgba(10, 177, 237, 0.2), -10px 0 15px rgba(20, 197, 247, 0.15)'
                                    }
                                }}>
                                <RowItem
                                    row={row}
                                    rowIndex={idx}
                                    onRowChange={updatedRow => {
                                        handleRowChange(idx, updatedRow)
                                    }}
                                    isDragged={draggedRowIndex === idx}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                        <Typography variant='subtitle2'>Table Columns</Typography>
                        <Button
                            startIcon={<AddRounded />}
                            size='small'
                            variant='outlined'
                            onClick={() => {
                                const newColumns = [
                                    ...(settings.columns ?? []),
                                    {
                                        uuid: crypto.randomUUID(),
                                        label: `Column ${(settings.columns?.length ?? 0) + 1}`,
                                        key: ''
                                    }
                                ]
                                onChange({ ...settings, columns: newColumns })
                            }}>
                            Column
                        </Button>
                    </Box>
                    {(settings.columns ?? []).map((col: IReportTableColumn, index: number) => (
                        <Box
                            key={col.uuid}
                            sx={{
                                'position': 'relative',
                                'transition': 'all 0.2s ease-in-out',
                                'transform': dragOverColumnIndex === index ? 'translateY(-4px)' : 'translateY(0)',
                                '&::before': dragOverColumnIndex === index
                                    ? {
                                            content: '""',
                                            position: 'absolute',
                                            top: -2,
                                            left: 0,
                                            right: 0,
                                            height: 4,
                                            backgroundColor: 'primary.main',
                                            borderRadius: 2,
                                            zIndex: 1000
                                        }
                                    : {}
                            }}
                            onDragOver={e => handleColumnDragOver(e, index)}
                            onDragLeave={handleColumnDragLeave}
                            onDrop={e => handleColumnDrop(e, index)}>
                            <Box
                                draggable
                                onDragStart={e => handleColumnDragStart(e, index)}
                                onDragEnd={handleColumnDragEnd}
                                sx={{
                                    'cursor': 'grab',
                                    '&:active': {
                                        cursor: 'grabbing'
                                    },
                                    'opacity': draggedColumnIndex === index ? 0.4 : 1,
                                    'transition': 'opacity 0.2s ease-in-out',
                                    '&:hover': {
                                        '.report-section-commands': { opacity: 1 },
                                        '.drag-handle': { opacity: 1 },
                                        'boxShadow': '10px 0 15px rgba(10, 177, 237, 0.2), -10px 0 15px rgba(20, 197, 247, 0.15)'

                                    }
                                }}>
                                <ColumnAccordion
                                    col={col}
                                    colIndex={index}
                                    onColChange={updatedCol => handleColumnChange(index, updatedCol)}
                                    isDragged={draggedColumnIndex === index}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Grid>
        </Grid>
    )
}

interface RowItemP {
    row: IReportTableRow
    rowIndex: number
    onRowChange: (updatedRow: IReportTableRow) => void
    isDragged?: boolean
}

const RowItem = ({ row, rowIndex, onRowChange, isDragged }: RowItemP) => {
    if (row.isSpacer) {
        return (
            <Box sx={{
                mb: 2,
                p: 2,
                height: '42.5px',
                border: isDragged ? '2px dashed' : '1px dashed',
                borderColor: isDragged ? 'primary.main' : 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: isDragged ? 'action.hover' : 'transparent'
            }}>
                <Box display='flex' alignItems='center' gap={1}>
                    <Typography variant='subtitle2' color='text.secondary'>
                        Spacer
                    </Typography>
                </Box>
                <IconButton
                    style={{ marginRight: 22 }}
                    size='small'
                    color='error'
                    onClick={() => onRowChange(null)}>
                    <DeleteIcon />
                </IconButton>
            </Box>
        )
    }

    return (
        <DataSetAccordion
            row={row}
            rowIndex={rowIndex}
            onRowChange={onRowChange}
            isDragged={isDragged}
        />
    )
}

interface DataSetAccordionP {
    row: IReportTableRow
    rowIndex: number
    onRowChange: (updatedRow: IReportTableRow | null) => void
    isDragged?: boolean
}

const DataSetAccordion = ({ row, rowIndex, onRowChange, isDragged }: DataSetAccordionP) => {
    return (
        <Box sx={{ mb: 2 }}>
            <Accordion sx={{
                'boxShadow': 1,
                'border': isDragged ? '2px dashed' : 'none',
                'borderColor': isDragged ? 'primary.main' : 'transparent',
                '& .MuiAccordionSummary-root': {
                    minHeight: 36,
                    paddingY: 0.5,
                    backgroundColor: isDragged ? 'action.hover' : 'transparent'
                },
                '& .MuiAccordionDetails-root': { paddingY: 1 }
            }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 'minHeight': 36, 'paddingY': 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', marginY: 0 } }}>
                    <Typography sx={{ flexGrow: 1 }}>
                        {row.value || `Data Set ${rowIndex + 1}`}
                    </Typography>
                    <IconButton
                        size='small'
                        color='error'
                        onClick={e => {
                            e.stopPropagation()
                            onRowChange(null)
                        }}>
                        <DeleteIcon />
                    </IconButton>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                value={row.value}
                                label='Data Set Value'
                                onValueChange={val => {
                                    onRowChange({ ...row, value: val })
                                }}
                                placeholder={`Data ${rowIndex + 1}`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 2 }}>
                                Rows Text Style
                            </Typography>
                            <TextTableEditor
                                style={row.rowStyle || {}}
                                onChange={style => onRowChange({ ...row, rowStyle: style })}
                                onResetStyle={() => {
                                    onRowChange({
                                        ...row,
                                        rowStyle: {}
                                    })
                                }}
                                textDecoration
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 2 }}>
                                Rows Number Style
                            </Typography>
                            <TextTableEditor
                                style={row.rowStyle || {}}
                                onChange={style => onRowChange({ ...row, rowStyle: style })}
                                numberFormatter
                                formatNumbers={row.formatNumbers}
                                formatNumbersFinancial={row.formatNumbersFinancial}
                                formatNumbersPercentage={row.formatNumbersPercentage}
                                roundDigits={row.roundDigits}
                                onFormatPercentageChanges={(formatNumbersPercentage, roundDigits) => onRowChange({
                                    ...row,
                                    formatNumbersPercentage,
                                    formatNumbers: false,
                                    formatNumbersFinancial: false,
                                    roundDigits,
                                    // @ts-ignore Remove old table column in dynamo db
                                    formatNumbersNoDollar: undefined
                                })}
                                onFormatNumberChanges={(formatNumbers, roundDigits) => {
                                    onRowChange({
                                        ...row,
                                        formatNumbers,
                                        formatNumbersPercentage: false,
                                        formatNumbersFinancial: false,
                                        roundDigits,
                                        // @ts-ignore Remove old table column in dynamo db
                                        formatNumbersNoDollar: undefined
                                    })
                                }}
                                onFormatFinancialChanges={(formatNumbersFinancial, roundDigits) => onRowChange({
                                    ...row,
                                    formatNumbersFinancial,
                                    formatNumbers: false,
                                    formatNumbersPercentage: false,
                                    roundDigits,
                                    // @ts-ignore Remove old table column in dynamo db
                                    formatNumbersNoDollar: undefined
                                })}
                                onRoundDigitsChange={roundDigits => onRowChange({ ...row, roundDigits })}
                                onResetStyle={() => {
                                    onRowChange({
                                        ...row,
                                        rowStyle: {}
                                    })
                                }}
                                onNumberFormatReset={() => {
                                    onRowChange({
                                        ...row,
                                        formatNumbers: false,
                                        formatNumbersPercentage: false,
                                        formatNumbersFinancial: false,
                                        roundDigits: null,
                                        // @ts-ignore Remove old table column in dynamo db
                                        formatNumbersNoDollar: undefined
                                    })
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 2 }}>
                                Border Style
                            </Typography>
                            <BorderStyleEditor
                                isDataSet
                                label='Data Set Borders'
                                borders={row.borders}
                                onChange={borders => onRowChange({ ...row, borders })}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}

interface ColumnAccordionP {
    col: IReportTableColumn
    colIndex: number
    isSubRow?: boolean
    onColChange: (updatedCol: IReportTableColumn | null) => void
    isDragged?: boolean
}

const ColumnAccordion = ({ col, colIndex, onColChange, isDragged }: ColumnAccordionP) => {
    return (
        <Box sx={{ mb: 2 }}>
            <Accordion sx={{
                'boxShadow': 1,
                'border': isDragged ? '2px dashed' : 'none',
                'borderColor': isDragged ? 'primary.main' : 'transparent',
                '& .MuiAccordionSummary-root': {
                    minHeight: 36,
                    paddingY: 0.5,
                    backgroundColor: isDragged ? 'action.hover' : 'transparent'
                },
                '& .MuiAccordionDetails-root': { paddingY: 1 }
            }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 'minHeight': 36, 'paddingY': 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', marginY: 0 } }}>
                    <Typography sx={{ flexGrow: 1 }}>
                        {col.label ?? `Column ${colIndex + 1}`}
                    </Typography>
                    <IconButton
                        size='small'
                        color='error'
                        onClick={e => {
                            e.stopPropagation()
                            onColChange(null)
                        }}>
                        <DeleteIcon />
                    </IconButton>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2} paddingBottom={2}>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                label='Label'
                                value={col.label}
                                onValueChange={label => onColChange({ ...col, label })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                label='Key'
                                disabled={col.subColumns?.length > 0}
                                value={col.subColumns?.length > 0 ? '' : (col?.key ?? '')}
                                onValueChange={key => onColChange({ ...col, key })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                label='Width'
                                type='number'
                                value={col?.width?.toString() ?? ''}
                                onValueChange={width => onColChange({ ...col, width: width ? Number(width) : undefined })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 2 }}>
                                Column Styles
                            </Typography>

                            <TextTableEditor
                                label='Header'
                                style={col.headerStyle ?? {}}
                                onChange={style => onColChange({ ...col, headerStyle: style })}
                                headersStyle
                            />
                            <TextTableEditor
                                label='Text'
                                style={col.cellStyle ?? {}}
                                onChange={style => onColChange({ ...col, cellStyle: style })}
                                textDecoration
                                textAlign
                            />

                            <TextTableEditor
                                label='Numbers'
                                style={col.cellStyle ?? {}}
                                onChange={style => onColChange({ ...col, cellStyle: style })}
                                numberFormatter
                                formatNumbers={col.formatNumbers}
                                formatNumbersFinancial={col.formatNumbersFinancial}
                                formatNumbersPercentage={col.formatNumbersPercentage}
                                roundDigits={col.roundDigits}
                                onFormatNumberChanges={(formatNumbers, roundDigits) => onColChange(
                                    {
                                        ...col,
                                        formatNumbers,
                                        formatNumbersPercentage: false,
                                        formatNumbersFinancial: false,
                                        roundDigits,
                                        // @ts-ignore Remove old table column in dynamo db
                                        formatNumbersNoDollar: undefined
                                    }
                                )}
                                onFormatPercentageChanges={(formatNumbersPercentage, roundDigits) => onColChange(
                                    {
                                        ...col,
                                        formatNumbersPercentage,
                                        formatNumbers: false,
                                        formatNumbersFinancial: false,
                                        roundDigits,
                                        // @ts-ignore Remove old table column in dynamo db
                                        formatNumbersNoDollar: undefined
                                    }
                                )}
                                onFormatFinancialChanges={(formatNumbersFinancial, roundDigits) => onColChange(
                                    {
                                        ...col,
                                        formatNumbersFinancial,
                                        formatNumbers: false,
                                        formatNumbersPercentage: false,
                                        roundDigits,
                                        // @ts-ignore Remove old table column in dynamo db
                                        formatNumbersNoDollar: undefined
                                    }
                                )}
                                onRoundDigitsChange={roundDigits => onColChange({ ...col, roundDigits })}
                                onResetStyle={() => {
                                    onColChange({
                                        ...col,
                                        cellStyle: {}
                                    })
                                }}
                                onNumberFormatReset={() => {
                                    onColChange({
                                        ...col,
                                        formatNumbers: false,
                                        formatNumbersPercentage: false,
                                        formatNumbersFinancial: false,
                                        roundDigits: null,
                                        // @ts-ignore Remove old table column in dynamo db
                                        formatNumbersNoDollar: undefined
                                    })
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 2 }}>
                                Border Style
                            </Typography>
                            <BorderStyleEditor
                                isColumn
                                label='Column Borders'
                                borders={col.borders}
                                onChange={borders => onColChange({ ...col, borders })}
                            />
                        </Grid>

                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}

export default TableEditor
